import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowRight,
  Star,
  Shield,
  Clock,
  Building2,
  MessageSquare,
} from "lucide-react";
import { Link } from "react-router-dom";

const BRAND = "#25d976";

export default function App() {
  return (
    <div
      style={{
        minHeight: "80vh",
        background: "#050505",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "24px",
        fontFamily: "sans-serif",
      }}
    >
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Syne:wght@400;500;600;700;800&family=Hind+Siliguri:wght@300;400;500;600;700&display=swap');
        * { box-sizing: border-box; margin: 0; padding: 0; }

        .rn-font-display { font-family: 'Syne', sans-serif !important; }
        .rn-font-body { font-family: 'Hind Siliguri', sans-serif !important; }

        .rn-noise {
          position: absolute; inset: 0; pointer-events: none; opacity: 0.04; border-radius: 24px;
          background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E");
          background-size: 180px 180px;
        }
        .rn-grid {
          position: absolute; inset: 0;
          background-image: linear-gradient(rgba(255,255,255,0.025) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.025) 1px, transparent 1px);
          background-size: 48px 48px;
        }

        .rn-btn::before {
          content: '';
          position: absolute;
          top: 0; left: -100%; width: 60%; height: 100%;
          background: linear-gradient(90deg, transparent, rgba(255,255,255,0.18), transparent);
          transition: left 0.65s ease;
        }
        .rn-btn:hover::before { left: 160%; }

        @keyframes orb1 {
          0%,100% { transform: translate(0,0) scale(1); }
          33% { transform: translate(28px,-18px) scale(1.08); }
          66% { transform: translate(-18px,14px) scale(0.95); }
        }
        @keyframes orb2 {
          0%,100% { transform: translate(0,0) scale(1); }
          33% { transform: translate(-22px,18px) scale(1.06); }
          66% { transform: translate(18px,-12px) scale(0.97); }
        }
        .orb1 { animation: orb1 12s ease-in-out infinite; }
        .orb2 { animation: orb2 16s ease-in-out infinite; }

        @keyframes pulseRing {
          0% { transform: scale(1); opacity: 0.75; }
          100% { transform: scale(2.5); opacity: 0; }
        }
        .pulse-dot { position: relative; }
        .pulse-dot::after {
          content: ''; position: absolute; inset: 0; border-radius: 9999px;
          background: ${BRAND};
          animation: pulseRing 2s ease-out infinite;
        }

        .stat-card {
          background: rgba(255,255,255,0.03);
          border: 1px solid rgba(255,255,255,0.07);
          transition: background 0.3s, border-color 0.3s;
        }
        .stat-card:hover {
          background: rgba(37,217,118,0.06);
          border-color: rgba(37,217,118,0.2);
        }
        .progress-dot {
          height: 8px; border-radius: 999px;
          transition: all 0.4s cubic-bezier(0.22,1,0.36,1);
          border: none; cursor: pointer;
          outline: none;
        }
        .progress-dot:focus { outline: none; }
      `}</style>

      <div style={{ width: "100%", maxWidth: 920 }}>
        <RatingNetworkCTA />
      </div>
    </div>
  );
}

function RatingNetworkCTA() {
  const steps = useMemo(
    () => [
      { text: "ইউনিভার্সিটিতে পরো?" },
      { text: "কেমন তোমার ফ্যাকাল্টি?" },
      { text: "অভিজ্ঞতা শেয়ার করো!" },
    ],
    [],
  );

  const [idx, setIdx] = useState(0);
  const [hovered, setHovered] = useState(false);

  useEffect(() => {
    const t = setInterval(() => setIdx((p) => (p + 1) % steps.length), 3000);
    return () => clearInterval(t);
  }, [steps.length]);

  return (
    <motion.section
      style={{ position: "relative", width: "100%" }}
      initial={{ opacity: 0, y: 40 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.75, ease: [0.22, 1, 0.36, 1] }}
    >
      {/* Outer glow border */}
      <div
        style={{
          position: "absolute",
          inset: -1,
          borderRadius: 25,
          pointerEvents: "none",
          background:
            "linear-gradient(135deg, rgba(37,217,118,0.14), rgba(0,0,0,0) 55%, rgba(14,165,233,0.07))",
          boxShadow: `0 0 0 1px rgba(37,217,118,0.12), 0 20px 60px rgba(37,217,118,0.1), 0 40px 80px rgba(0,0,0,0.6)`,
        }}
      />

      {/* Card */}
      <div
        style={{
          position: "relative",
          borderRadius: 24,
          overflow: "hidden",
          background: "#0b0b0b",
          border: "1px solid rgba(255,255,255,0.07)",
        }}
      >
        <div className="rn-noise" />
        <div className="rn-grid" />

        {/* Orbs */}
        <div
          style={{
            position: "absolute",
            inset: 0,
            overflow: "hidden",
            borderRadius: 24,
            pointerEvents: "none",
          }}
        >
          <div
            className="orb1"
            style={{
              position: "absolute",
              top: -100,
              left: -100,
              width: 280,
              height: 280,
              borderRadius: "50%",
              opacity: 0.18,
              background: `radial-gradient(circle, ${BRAND}, transparent 70%)`,
              filter: "blur(40px)",
            }}
          />
          <div
            className="orb2"
            style={{
              position: "absolute",
              bottom: -120,
              right: -120,
              width: 360,
              height: 360,
              borderRadius: "50%",
              opacity: 0.1,
              background: "radial-gradient(circle, #0ea5e9, transparent 70%)",
              filter: "blur(60px)",
            }}
          />
        </div>

        {/* Inner content */}
        <div
          className="rn-font-body"
          style={{ position: "relative", padding: "clamp(28px, 5vw, 56px)" }}
        >
          {/* Top row */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              flexWrap: "wrap",
              gap: 16,
              marginBottom: 40,
            }}
          >
            {/* Badge */}
            <motion.div
              initial={{ opacity: 0, x: -14 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 10,
                padding: "8px 18px",
                borderRadius: 999,
                background: "rgba(37,217,118,0.08)",
                border: "1px solid rgba(37,217,118,0.18)",
                backdropFilter: "blur(12px)",
              }}
            >
              <span
                className="pulse-dot"
                style={{
                  position: "relative",
                  display: "inline-block",
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  background: BRAND,
                  flexShrink: 0,
                }}
              />
              <span
                className="rn-font-display"
                style={{
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: "0.12em",
                  textTransform: "uppercase",
                  color: BRAND,
                }}
              >
                Goat Teachers
              </span>
            </motion.div>

            {/* Stats */}
            <motion.div
              initial={{ opacity: 0, x: 14 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.3 }}
              style={{ display: "flex", alignItems: "center", gap: 12 }}
            >
              <StatPill
                icon={<MessageSquare size={12} color={BRAND} />}
                value="২.৫K+"
                label="রিভিউ"
              />
              <div
                style={{
                  width: 1,
                  height: 32,
                  background: "rgba(255,255,255,0.08)",
                }}
              />
              <StatPill
                icon={<Building2 size={12} color={BRAND} />}
                value="৩৫+"
                label="প্রতিষ্ঠান"
              />
            </motion.div>
          </div>

          {/* Main body */}
          <div
            style={{
              display: "flex",
              alignItems: "flex-end",
              justifyContent: "space-between",
              flexWrap: "wrap",
              gap: 36,
            }}
          >
            {/* Headline block */}
            <div style={{ flex: "1 1 300px" }}>
              <div
                style={{
                  position: "relative",
                  minHeight: "clamp(80px,12vw,140px)",
                  marginBottom: 14,
                }}
              >
                <AnimatePresence mode="wait">
                  <motion.div
                    key={idx}
                    initial={{ opacity: 0, y: 18, filter: "blur(8px)" }}
                    animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
                    exit={{ opacity: 0, y: -14, filter: "blur(5px)" }}
                    transition={{ duration: 0.52, ease: [0.22, 1, 0.36, 1] }}
                    style={{ position: "absolute", inset: 0 }}
                  >
                    <h2
                      className="rn-font-display"
                      style={{
                        fontSize: "clamp(1.85rem, 5vw, 3.75rem)",
                        fontWeight: 800,
                        lineHeight: 1.2,
                        paddingTop: "0.12em",
                        letterSpacing: "-0.02em",
                        background: `linear-gradient(135deg, #ffffff 15%, ${BRAND} 55%, #a7f3d0 90%)`,
                        WebkitBackgroundClip: "text",
                        WebkitTextFillColor: "transparent",
                        backgroundClip: "text",
                      }}
                    >
                      {steps[idx].text}
                    </h2>
                    <p
                      style={{
                        marginTop: 8,
                        fontSize: 14,
                        color: "rgba(255,255,255,0.35)",
                        fontFamily: "'Hind Siliguri', sans-serif",
                      }}
                    >
                      {steps[idx].sub}
                    </p>
                  </motion.div>
                </AnimatePresence>
              </div>

              <p
                style={{
                  fontSize: "clamp(13px, 1.8vw, 16px)",
                  color: "rgba(255,255,255,0.42)",
                  lineHeight: 1.75,
                  maxWidth: 480,
                }}
              >
                নিজের University ও Department-এর সম্পর্কে Review দিয়ে অন্যদের
                সঠিক সিদ্ধান্ত নিতে সাহায্য করো।
              </p>

              {/* Progress dots */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  marginTop: 24,
                }}
              >
                {steps.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setIdx(i)}
                    className="progress-dot"
                    style={{
                      width: i === idx ? 40 : 8,
                      background: i === idx ? BRAND : "rgba(255,255,255,0.12)",
                      boxShadow: i === idx ? `0 0 10px ${BRAND}70` : "none",
                    }}
                  />
                ))}
              </div>
            </div>

            {/* CTA block */}
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{
                delay: 0.4,
                duration: 0.6,
                ease: [0.22, 1, 0.36, 1],
              }}
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 12,
                minWidth: 220,
              }}
            >
              {/* Feature pills */}
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <FeaturePill
                  icon={<Shield size={11} color={BRAND} />}
                  text="Complete Anonymity
"
                />
                <FeaturePill
                  icon={<Star size={11} color={BRAND} />}
                  text="Top 35 Universities"
                />
              </div>

              {/* CTA Button */}

              <Link
                to="/rating-network"
                onClick={() =>
                  window.scrollTo({ top: 0, left: 0, behavior: "auto" })
                }
                style={{ textDecoration: "none" }}
              >
                <button
                  onMouseEnter={() => setHovered(true)}
                  onMouseLeave={() => setHovered(false)}
                  className="rn-btn rn-font-display"
                  style={{
                    position: "relative",
                    overflow: "hidden",
                    background: hovered
                      ? `linear-gradient(135deg, #32ff8a, ${BRAND})`
                      : `linear-gradient(135deg, ${BRAND}, #1db863)`,
                    color: "#000",
                    border: "none",
                    borderRadius: 16,
                    padding: "14px 28px",
                    fontSize: 15,
                    fontWeight: 800,
                    letterSpacing: "0.01em",
                    cursor: "pointer",
                    boxShadow: hovered
                      ? `0 8px 32px ${BRAND}55, 0 2px 8px rgba(0,0,0,0.35)`
                      : `0 4px 20px ${BRAND}30`,
                    transform: hovered
                      ? "scale(1.025) translateY(-1px)"
                      : "scale(1) translateY(0)",
                    transition: "all 0.3s cubic-bezier(0.22,1,0.36,1)",
                  }}
                >
                  <span
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 10,
                    }}
                  >
                    অভিজ্ঞতা শেয়ার করো
                    <motion.span
                      animate={{ x: hovered ? 4 : 0 }}
                      transition={{ duration: 0.25 }}
                    >
                      <ArrowRight size={17} strokeWidth={2.5} />
                    </motion.span>
                  </span>
                </button>
              </Link>

              {/* Time hint */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 6,
                  color: "rgba(255,255,255,0.28)",
                }}
              >
                <Clock size={12} />
                <span
                  style={{
                    fontSize: 12,
                    fontFamily: "'Hind Siliguri', sans-serif",
                  }}
                >
                  মাত্র ১ মিনিটে সম্পন্ন করুন
                </span>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Bottom accent */}
        <div
          style={{
            height: 1,
            width: "100%",
            background: `linear-gradient(90deg, transparent, ${BRAND}55, transparent)`,
          }}
        />
      </div>
    </motion.section>
  );
}

function StatPill({ icon, value, label }) {
  return (
    <div
      className="stat-card"
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 10,
        padding: "8px 14px",
        borderRadius: 12,
        cursor: "default",
      }}
    >
      {icon}
      <div>
        <div
          className="rn-font-display"
          style={{
            color: "#fff",
            fontSize: 13,
            fontWeight: 700,
            lineHeight: 1,
          }}
        >
          {value}
        </div>
        <div
          style={{
            fontSize: 11,
            marginTop: 2,
            color: "rgba(255,255,255,0.32)",
            fontFamily: "'Hind Siliguri', sans-serif",
          }}
        >
          {label}
        </div>
      </div>
    </div>
  );
}

function FeaturePill({ icon, text }) {
  return (
    <div
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        padding: "6px 12px",
        borderRadius: 8,
        fontSize: 12,
        fontWeight: 500,
        background: "rgba(255,255,255,0.04)",
        border: "1px solid rgba(255,255,255,0.07)",
        color: "rgba(255,255,255,0.45)",
        fontFamily: "'Hind Siliguri', sans-serif",
      }}
    >
      {icon}
      {text}
    </div>
  );
}
